// SignIn.jsx — the way into the portal.
//
// ── WHY TWO PANELS AND NOT A CARD ──
// A centred white card on a tinted page is what every admin template ships
// with, and it says nothing: at a glance you cannot tell whose portal you have
// opened. The brand panel carries that, and it gives the page somewhere to put
// weight that is not competing with a form of two fields.
//
// ── WHY THERE IS NO STACK BADGE ──
// This screen used to show ".NET · PostgreSQL · schema 49 · api.milliyo.com"
// above the form. It was put there to catch a build pointed at the wrong API,
// which is a real problem — but it announced the framework, the database and
// the schema version to anyone who loaded the login page, which is exactly the
// list someone probing the box would like to start from. The check is still
// made; it is simply silent unless it fails. Healthy says nothing, unreachable
// says so plainly, and the CORS canary it exists to be still fires.
//
// ── THE SECOND FACTOR ──
// This screen used to end at `dispatch(adminLogin)` and navigate on fulfilled.
// That is wrong for any account with 2FA on: those sign-ins answer 200 with a
// challenge and NO token, so the navigate fired, ProtectedLayout found no
// session and bounced straight back here. The slice has carried `pending2fa`
// and the verify/enrol thunks since the cutover and nothing read them, which
// means two-factor accounts could not get in at all.

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  adminLogin,
  verifyAdmin2fa,
  setupAdmin2fa,
  enableAdmin2fa,
} from "../../../slice/adminUser/adminUser";
import Logo from "../../../assets/logo.png";
import SentrioLogo from "../../../assets/sentrio-logo.png";
import "../../../styles/portal-auth.css";

export default function SignIn() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { token, loading, error, pending2fa } = useSelector((s) => s.adminUser);

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [peek, setPeek] = useState(false);
  const [caps, setCaps] = useState(false);
  const [code, setCode] = useState("");

  /* Enrolment only: the secret the server minted for this challenge. */
  const [enrolment, setEnrolment] = useState(null);
  const [enrolError, setEnrolError] = useState(null);
  const [backupCodes, setBackupCodes] = useState(null);

  useEffect(() => {
    if (token) navigate("/admin/dashboard");
  }, [token, navigate]);

  /* An enrolment challenge needs a secret before anything can be typed, so it
     is fetched as soon as the server asks for one rather than sitting behind a
     second button whose purpose nobody would guess. */
  useEffect(() => {
    if (pending2fa?.mode !== "enrol" || enrolment) return;
    let alive = true;
    dispatch(setupAdmin2fa({ challenge: pending2fa.challenge })).then((r) => {
      if (!alive) return;
      if (r.meta.requestStatus === "fulfilled") setEnrolment(r.payload);
      else setEnrolError(r.payload || "Could not start two-factor setup.");
    });
    return () => {
      alive = false;
    };
  }, [pending2fa, enrolment, dispatch]);

  const submitCredentials = async (e) => {
    e.preventDefault();
    if (!username || !password) return;
    const result = await dispatch(adminLogin({ username, password }));
    /* Deliberately no navigate here. Either a token arrived, and the effect
       above moves on, or a challenge did, and this screen asks for the code. */
    if (result.meta.requestStatus === "fulfilled") setPassword("");
  };

  const submitCode = async (e) => {
    e.preventDefault();
    if (code.length !== 6) return;

    if (pending2fa.mode === "verify") {
      await dispatch(verifyAdmin2fa({ challenge: pending2fa.challenge, code }));
      setCode("");
      return;
    }

    const result = await dispatch(
      enableAdmin2fa({ challenge: pending2fa.challenge, code })
    );
    setCode("");
    /* Enabling does not mint a session — the backup codes are shown once, then
       the password is asked for again. */
    if (result.meta.requestStatus === "fulfilled") {
      setBackupCodes(result.payload?.backupCodes || []);
    }
  };

  const startOver = () => {
    setCode("");
    setEnrolment(null);
    setEnrolError(null);
    setBackupCodes(null);
    window.location.reload();
  };

  return (
    <div className="signin">
      <BrandPanel />

      <div className="signin__form">
        <div className="signin__panel">
          <ApiTrouble />

          {backupCodes ? (
            <BackupCodes codes={backupCodes} onDone={startOver} />
          ) : pending2fa ? (
            <SecondFactor
              mode={pending2fa.mode}
              username={pending2fa.username}
              code={code}
              setCode={setCode}
              onSubmit={submitCode}
              loading={loading}
              error={error || enrolError}
              enrolment={enrolment}
              onBack={startOver}
            />
          ) : (
            <Credentials
              username={username}
              setUsername={setUsername}
              password={password}
              setPassword={setPassword}
              peek={peek}
              setPeek={setPeek}
              caps={caps}
              setCaps={setCaps}
              onSubmit={submitCredentials}
              loading={loading}
              error={error}
            />
          )}

          <div className="signin__foot">
            <button
              type="button"
              className="signin__link"
              onClick={() => navigate("/privacypolicy")}
            >
              Privacy Policy
            </button>
            <div className="signin__by">
              <span>Powered by</span>
              <img src={SentrioLogo} alt="Sentrio" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── step one ────────────────────────────────────────────────────────────── */

function Credentials({
  username,
  setUsername,
  password,
  setPassword,
  peek,
  setPeek,
  caps,
  setCaps,
  onSubmit,
  loading,
  error,
}) {
  /* Caps lock accounts for a decent share of "wrong password", and the server
     cannot tell you which it was. The browser can. */
  const watchCaps = (e) => {
    if (typeof e.getModifierState === "function") {
      setCaps(e.getModifierState("CapsLock"));
    }
  };

  return (
    <form onSubmit={onSubmit}>
      <div className="signin__step">
        <p className="signin__eyebrow">MilliyO Console</p>
        <h1 className="signin__heading">Sign in</h1>
        <p className="signin__sub">
          Orders, stock, milling and the till — all in one place.
        </p>
      </div>

      {error ? (
        <p className="signin__error">
          <Warn />
          <span>{error}</span>
        </p>
      ) : null}

      <div className="signin__step signin__field">
        <label className="signin__label" htmlFor="signin-user">
          Username
        </label>
        <input
          id="signin-user"
          className="signin__input"
          type="text"
          autoComplete="username"
          autoFocus
          placeholder="Your username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          required
        />
      </div>

      <div className="signin__step signin__field">
        <label className="signin__label" htmlFor="signin-pass">
          Password
        </label>
        <input
          id="signin-pass"
          className="signin__input"
          type={peek ? "text" : "password"}
          autoComplete="current-password"
          placeholder="••••••••"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyUp={watchCaps}
          onKeyDown={watchCaps}
          onBlur={() => setCaps(false)}
          required
          style={{ paddingRight: 70 }}
        />
        <button
          type="button"
          className="signin__peek"
          onClick={() => setPeek((v) => !v)}
          tabIndex={-1}
        >
          {peek ? "Hide" : "Show"}
        </button>
      </div>

      {caps ? (
        <p className="signin__caps">
          <Warn /> Caps lock is on
        </p>
      ) : null}

      <div className="signin__step">
        <button type="submit" className="signin__go" disabled={loading}>
          {loading ? (
            <>
              <span className="signin__spin" /> Checking…
            </>
          ) : (
            <>
              Sign in
              <Arrow />
            </>
          )}
        </button>
      </div>
    </form>
  );
}

/* ── step two ────────────────────────────────────────────────────────────── */

function SecondFactor({
  mode,
  username,
  code,
  setCode,
  onSubmit,
  loading,
  error,
  enrolment,
  onBack,
}) {
  const enrolling = mode === "enrol";

  return (
    <form onSubmit={onSubmit}>
      <div className="signin__step">
        <p className="signin__eyebrow">
          {enrolling ? "Two-factor setup" : "Two-factor"}
        </p>
        <h1 className="signin__heading">
          {enrolling ? "Add your authenticator" : "One more step"}
        </h1>
        <p className="signin__sub">
          {enrolling
            ? "This account must use an authenticator app. Add the key below to one, then type the six digits it shows."
            : `Enter the six digits from your authenticator app for ${username}.`}
        </p>
      </div>

      {error ? (
        <p className="signin__error">
          <Warn />
          <span>{error}</span>
        </p>
      ) : null}

      {enrolling ? (
        <div className="signin__step signin__note">
          {enrolment ? (
            <>
              In Google Authenticator, 1Password or any TOTP app choose “enter a
              setup key” and paste this:
              <code className="signin__secret">{enrolment.secret}</code>
            </>
          ) : (
            "Fetching your setup key…"
          )}
        </div>
      ) : null}

      <div className="signin__step signin__field">
        <label className="signin__label" htmlFor="signin-code">
          Six-digit code
        </label>
        <input
          id="signin-code"
          className="signin__input signin__input--code"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          autoFocus
          maxLength={6}
          placeholder="••••••"
          value={code}
          onChange={(e) =>
            setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
          }
        />
      </div>

      <div className="signin__step">
        <button
          type="submit"
          className="signin__go"
          disabled={loading || code.length !== 6 || (enrolling && !enrolment)}
        >
          {loading ? (
            <>
              <span className="signin__spin" /> Checking…
            </>
          ) : (
            <>
              {enrolling ? "Turn on two-factor" : "Verify"}
              <Arrow />
            </>
          )}
        </button>
        <button type="button" className="signin__back" onClick={onBack}>
          ← Use a different account
        </button>
      </div>
    </form>
  );
}

/* Shown once, and never again — so they cannot be navigated past by accident. */
function BackupCodes({ codes, onDone }) {
  return (
    <div className="signin__step">
      <p className="signin__eyebrow">Two-factor is on</p>
      <h1 className="signin__heading">Keep these somewhere safe</h1>
      <p className="signin__sub">
        Each one signs you in once if you lose the authenticator. They are not
        shown again.
      </p>
      <div className="signin__note">
        {codes.map((c) => (
          <code key={c} className="signin__secret">
            {c}
          </code>
        ))}
      </div>
      <button type="button" className="signin__go" onClick={onDone}>
        I have saved them — sign in
      </button>
    </div>
  );
}

/* ── the brand side ──────────────────────────────────────────────────────── */

function BrandPanel() {
  return (
    <aside className="signin__brand">
      <span className="signin__badge">
        <img src={Logo} alt="MilliyO" />
      </span>

      <div>
        <div className="signin__rule" />
        <h2 className="signin__wordmark">
          Into today’s kitchen,
          <br />
          <em>for tomorrow’s generation</em>
        </h2>
        <p className="signin__blurb">
          Flour milled the day it is ordered, stock counted where it sits, and
          every rupee on one ledger.
        </p>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: 20,
          flexWrap: "wrap",
        }}
      >
        <ul className="signin__facts">
          <li>Fresh milling</li>
          <li>Live stock</li>
          <li>One ledger</li>
        </ul>
        <p className="signin__tagline">Shop console</p>
      </div>
    </aside>
  );
}

/* ── the API check, seen only when it fails ───────────────────────────────
   A build pointed at the wrong API, or an API that is down, looks from here
   exactly like one that works — until a password is typed and rejected for a
   reason nobody can act on. This asks /health once on load.

   It is also the only check in the stack that exercises the browser's CORS
   path. Every cutover check ran through curl, which does not preflight, so a
   missing Access-Control-Allow-Origin passed all of them and would have left
   the portal dead with no clue why.

   What it does NOT do is report success. The version that did printed the
   framework, the database and the schema number above the password box.      */
function ApiTrouble() {
  const [trouble, setTrouble] = useState(null);

  useEffect(() => {
    const base = process.env.REACT_APP_API_URL || "";
    let alive = true;
    // No auth header: /health is anonymous, so a failure here is genuinely
    // "cannot reach the API", not "not signed in".
    fetch(`${base}/health`, { headers: { Accept: "application/json" } })
      .then((r) =>
        r.ok ? r.json() : Promise.reject(new Error(String(r.status)))
      )
      .then((d) => {
        if (!alive) return;
        setTrouble(
          d && d.service === "milliyo-ledger"
            ? null
            : "This portal is pointed at something that is not the MilliyO API. Signing in will not work until that is fixed."
        );
      })
      .catch(
        () =>
          alive &&
          setTrouble(
            "The server is not answering. Check your connection — if it is fine, the API is down and signing in will fail."
          )
      );
    return () => {
      alive = false;
    };
  }, []);

  if (!trouble) return null;

  return (
    <p className="signin__offline">
      <Warn />
      <span>{trouble}</span>
    </p>
  );
}

/* ── glyphs ──────────────────────────────────────────────────────────────── */

function Warn() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2.1a9.9 9.9 0 1 0 0 19.8 9.9 9.9 0 0 0 0-19.8Zm0 4.3a1.2 1.2 0 0 1 1.2 1.2v5a1.2 1.2 0 0 1-2.4 0v-5A1.2 1.2 0 0 1 12 6.4Zm0 9a1.3 1.3 0 1 1 0 2.6 1.3 1.3 0 0 1 0-2.6Z" /></svg>
  );
}

function Arrow() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M13.4 4.6a1.2 1.2 0 0 1 1.7 0l6.4 6.4a1.2 1.2 0 0 1 0 1.7l-6.4 6.4a1.2 1.2 0 1 1-1.7-1.7l4.3-4.3H4a1.2 1.2 0 0 1 0-2.4h13.7l-4.3-4.3a1.2 1.2 0 0 1 0-1.8Z" /></svg>
  );
}
